import { Head } from 'vite-react-ssg';
import { company } from '@/data/company';
import { reviews } from '@/data/reviews';

const SITE_URL = (import.meta.env.VITE_SITE_URL ?? company.url).replace(/\/$/, '');

type JsonLd = Record<string, unknown>;

type SeoProps = {
  title: string;
  description: string;
  /** Path beginning with `/`. Used to build the canonical and og:url. */
  path: string;
  /** Page-specific social image - relative or absolute. Defaults to /og-default.jpg. */
  image?: string;
  /** Optional descriptive alt text for the social image. */
  imageAlt?: string;
  /** Marks the document as noindex,nofollow (e.g. 404, admin). */
  noindex?: boolean;
  /** One or more JSON-LD blocks to append to <head>. */
  jsonLd?: JsonLd | JsonLd[];
  /** Override the default ogType (article, profile, etc.). */
  ogType?: 'website' | 'article' | 'profile';
  /** Optional list of comma-separated keywords. Low SEO value, but harmless and used by some social tools. */
  keywords?: string;
  /** Optional ISO date used for og:updated_time and article:modified_time. */
  updatedAt?: string;
};

/**
 * Builds a canonical, absolute URL for a path on the production domain.
 * Trims trailing slashes for consistency, but preserves the root `/`.
 */
function absoluteUrl(path: string): string {
  if (!path.startsWith('/')) path = `/${path}`;
  if (path.length > 1) path = path.replace(/\/$/, '');
  return `${SITE_URL}${path === '/' ? '' : path}`;
}

function absoluteImage(image: string | undefined): string {
  if (!image) return `${SITE_URL}/og-default.jpg`;
  if (/^https?:\/\//i.test(image)) return image;
  return `${SITE_URL}${image.startsWith('/') ? image : `/${image}`}`;
}

export function Seo({
  title,
  description,
  path,
  image,
  imageAlt,
  noindex,
  jsonLd,
  ogType = 'website',
  keywords,
  updatedAt,
}: SeoProps) {
  const url = absoluteUrl(path);
  // Avoid double-branding when the page title already references the company.
  const titleHasBrand =
    title.includes(company.legalName) || title.includes(company.shortName);
  const fullTitle = titleHasBrand ? title : `${title} | ${company.legalName}`;
  const ogImage = absoluteImage(image);
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  // For the social image alt, use the supplied alt or strip any brand from the
  // title to keep it punchy: "Roofing Services in Essex" rather than
  // "Bliss Building Contractors Ltd - Bliss Building Contractors Ltd | ...".
  const cleanTitle = title
    .replace(new RegExp(`\\s*\\|\\s*${company.legalName}\\s*$`), '')
    .replace(new RegExp(`^${company.legalName}\\s*\\|\\s*`), '')
    .replace(new RegExp(`^${company.shortName}\\s*\\|\\s*`), '');
  const altText = imageAlt ?? `${cleanTitle} | ${company.legalName}`;

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'} />
      <meta name="googlebot" content={noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large,max-snippet:-1'} />
      <meta name="bingbot" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={company.legalName} />
      <meta name="publisher" content={company.legalName} />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="geo.region" content="GB-ESS" />
      <meta name="geo.placename" content={company.hub} />
      <meta name="ICBM" content="51.8780,0.5500" />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={altText} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:site_name" content={company.legalName} />
      {updatedAt && <meta property="og:updated_time" content={updatedAt} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={altText} />

      {/* Facebook page reference for Open Graph attribution */}
      <meta property="article:publisher" content={company.socials.facebook} />

      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Head>
  );
}

/* -------------------------------------------------------------------------- */
/*  JSON-LD builders                                                          */
/* -------------------------------------------------------------------------- */

function aggregateRating(): JsonLd {
  return {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    bestRating: '5',
    worstRating: '1',
    reviewCount: String(reviews.length),
  };
}

function reviewItems(): JsonLd[] {
  return reviews.map((r) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: r.author },
    reviewBody: r.body,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
      worstRating: '1',
    },
    publisher: { '@type': 'Organization', name: r.source },
  }));
}

/**
 * GeneralContractor / LocalBusiness JSON-LD with full areaServed,
 * opening hours, sameAs (socials) and aggregate rating.
 *
 * Pass `withReviews` only on the homepage to avoid duplicating long
 * review arrays across every service page.
 */
export function localBusinessJsonLd({
  withReviews = false,
}: { withReviews?: boolean } = {}): JsonLd {
  const ld: JsonLd = {
    '@context': 'https://schema.org',
    '@type': ['GeneralContractor', 'RoofingContractor', 'LocalBusiness'],
    '@id': `${SITE_URL}/#business`,
    name: company.legalName,
    alternateName: company.shortName,
    description: company.description,
    image: [`${SITE_URL}/logo.png`, `${SITE_URL}/og-default.jpg`],
    logo: `${SITE_URL}/logo.png`,
    url: SITE_URL,
    telephone: company.phoneTel,
    email: company.email,
    priceRange: '££',
    currenciesAccepted: 'GBP',
    paymentAccepted: 'Cash, Bank transfer, Card',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Braintree',
      addressRegion: 'Essex',
      addressCountry: 'GB',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 51.8780,
      longitude: 0.5500,
    },
    areaServed: company.areasServed.map((a) => ({
      '@type': 'AdministrativeArea',
      name: a,
    })),
    serviceArea: company.serviceTowns.map((t) => ({
      '@type': 'City',
      name: t,
    })),
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '08:00',
        closes: '18:00',
      },
    ],
    sameAs: [
      company.socials.facebook,
      company.socials.twitter,
      company.socials.tiktok,
    ],
    aggregateRating: aggregateRating(),
    knowsAbout: [
      'Roofing',
      'New Roofs',
      'Roof Repairs',
      'Leadwork',
      'Damp Proofing',
      'Spray Foam Removal',
      'Driveways',
      'Block Paving',
      'Resin Bound Driveways',
      'Landscaping',
      'Patios',
      'Decking',
      'Fencing',
    ],
  };

  if (withReviews) {
    ld.review = reviewItems();
  }

  return ld;
}

export function organizationJsonLd(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: company.legalName,
    alternateName: company.shortName,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
      width: 512,
      height: 512,
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: company.phoneTel,
        contactType: 'customer service',
        areaServed: 'GB',
        availableLanguage: ['en-GB'],
        contactOption: 'TollFree',
      },
      {
        '@type': 'ContactPoint',
        telephone: company.mobileTel,
        contactType: 'emergency',
        areaServed: 'GB',
        availableLanguage: ['en-GB'],
      },
    ],
    sameAs: [
      company.socials.facebook,
      company.socials.twitter,
      company.socials.tiktok,
    ],
  };
}

export function websiteJsonLd(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: company.legalName,
    description: company.description,
    inLanguage: 'en-GB',
    publisher: { '@id': `${SITE_URL}/#organization` },
  };
}

export function serviceJsonLd(
  serviceName: string,
  serviceUrl: string,
  description: string,
  options?: { image?: string; subservices?: { title: string; url: string; description: string }[] },
): JsonLd {
  const ld: JsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceName,
    description,
    url: absoluteUrl(serviceUrl),
    serviceType: serviceName,
    image: absoluteImage(options?.image),
    provider: {
      '@type': 'GeneralContractor',
      '@id': `${SITE_URL}/#business`,
      name: company.legalName,
      url: SITE_URL,
      telephone: company.phoneTel,
    },
    areaServed: company.areasServed.map((a) => ({
      '@type': 'AdministrativeArea',
      name: a,
    })),
    aggregateRating: aggregateRating(),
  };

  if (options?.subservices && options.subservices.length > 0) {
    ld.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      name: serviceName,
      itemListElement: options.subservices.map((s, i) => ({
        '@type': 'Offer',
        position: i + 1,
        itemOffered: {
          '@type': 'Service',
          name: s.title,
          description: s.description,
          url: absoluteUrl(s.url),
        },
      })),
    };
  }

  return ld;
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: absoluteUrl(it.path),
    })),
  };
}

export function faqJsonLd(items: { question: string; answer: string }[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  };
}
